import { useEffect, useMemo, useState, useContext, useRef } from "react";
import ProfileService from "@services/api/profile";
import { UserContext } from "@contexts/User";
import config from "@config/config";
import customReportGraphqlInstance from "@services/api/customReport.graphql";
import { useMatch } from "react-router-dom";
import { MESSAGES } from "@router/consts";

// useProfileList is remounted on every component that lists accounts'
// students/counsellors (chat participant pickers, appointment booking,
// process creation, reminders, ...). Without this cache, switching
// between chats alone re-fetches the whole account's person list - a
// multi-MB payload - from scratch every time. Session-scoped (cleared on
// reload) and shared across every caller, so a profile created elsewhere
// in the tab won't appear here until the tab reloads.
const profileListCache = new Map();

const fetchProfileList = (
  accountId,
  type,
  isEligibleRouteToApplyAnonymousPicture,
  journalTypeId
) => {
  const cacheKey = `${accountId}|${type}|${isEligibleRouteToApplyAnonymousPicture}|${journalTypeId}`;
  if (!profileListCache.has(cacheKey)) {
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        type
      );
    const promise = (
      isUuid
        ? customReportGraphqlInstance.findAllUsersByCustomRolesId(type)
        : ProfileService.getProfilesByType(
            accountId,
            type,
            isEligibleRouteToApplyAnonymousPicture,
            journalTypeId
          )
    ).catch((err) => {
      profileListCache.delete(cacheKey);
      throw err;
    });
    profileListCache.set(cacheKey, promise);
  }
  return profileListCache.get(cacheKey);
};

export const useProfileList = (type, filterFn, journalTypeId = null) => {
  const [profiles, setProfiles] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useContext(UserContext);

  const isEligibleRouteToApplyAnonymousPicture = !!useMatch(MESSAGES);

  useEffect(() => {
    (async () => {
      if (!profile) {
        return;
      }

      setIsLoading(true);
      let profilesResponse = await fetchProfileList(
        profile.accountId,
        type,
        isEligibleRouteToApplyAnonymousPicture,
        journalTypeId
      );

      if (filterFn && typeof filterFn === "function") {
        profilesResponse = filterFn(profilesResponse);
      }

      setProfiles(profilesResponse);
      setIsLoading(false);
    })();
  }, [profile, type, journalTypeId]);

  return useMemo(() => {
    if (!profiles) return [[], { isLoading }];
    return [profiles, { isLoading }];
  }, [profiles, isLoading]);
};

export const useProfile = (profileId) => {
  const [profile, setProfile] = useState(null);
  const { profile: currentProfile } = useContext(UserContext);

  useEffect(() => {
    (async () => {
      if (!currentProfile || !profileId) {
        return;
      }

      const profileResponse = await ProfileService.getProfileById(
        currentProfile.accountId,
        profileId
      );

      setProfile(profileResponse);
    })();
  }, [currentProfile, profileId]);

  return useMemo(() => {
    if (!profile) {
      return null;
    }

    return profile;
  }, [currentProfile, profileId, profile]);
};

export const useProfileImageUrl = (profilePictureName, accountId) =>
  useMemo(() => {
    if (!profilePictureName || !accountId) {
      return "";
    }

    return `${config.resourceApiUrl}/accounts/${accountId}/profileImage/${profilePictureName}`;
  }, [profilePictureName, accountId]);

export const useProfileUrl = (profilePictureName, accountId) => {
  if (!profilePictureName || !accountId) {
    return "";
  }

  return `${config.resourceApiUrl}/accounts/${accountId}/profileImage/${profilePictureName}`;
};

export const useProfileVideoUrl = (profileVideoName, accountId) =>
  useMemo(() => {
    if (!profileVideoName || !accountId) {
      return "";
    }

    return `${config.resourceApiUrl}/accounts/${accountId}/introductionVideo/${profileVideoName}`;
  });
